import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import { durationMapping } from '../durationMapping';

import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import '@fortawesome/fontawesome-free/css/all.min.css';

import {
  useLazyGetEnergyConsumptionQuery,
  useLazyGetSeuEnergyConsumptionQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import { useTranslation } from 'react-i18next';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';

import { DateTime } from 'luxon';
import EnergyAnalysisSingleSeriesChart from '@/components/highcharts/powerAnalyser/EnergyAnalysisSingleSeriesChart';
import EnergyAnalysisSeuSingleSeriesChart from '@/components/highcharts/powerAnalyser/EnergyAnalysisSeuSingleSeriesChart';
import EnergyTreeNodeAutocomplete from '@/features/energyxplorer/components/EnergyTreeNodeAutocomplete';
import EnergyAnalyzerFilterDialog from '@/features/energyxplorer/components/EnergyAnalyzerFilterDialog';
import TimestampSummaryWarningIcon from '@/features/energyxplorer/components/TimestampsSummaryWarningIcon';
// import {
//   combineTimestampsSummaryList,
//   convertTimestampsSummaryToDateTimes,
// } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import TimestampSummarySeuWarningIcon from '@/features/energyxplorer/components/TimestampsSummarySeuWarningIcon';

const formatNumber = (number) => {
  return new Intl.NumberFormat('da-DK').format(number);
};

const getDurationConfig = (durationDays) => {
  return durationMapping.find(
    (entry) => durationDays >= entry.minDays && durationDays < entry.maxDays,
  );
};

const EnergyAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  setTimestampSummary,
  setTimestampSummarySetBy,
  duration,
  timeRange,
  focusedNode,
  focusedSeu,
  initialMode,
}) => {
  const dispatch = useDispatch();

  const [nodeToAnalyse, setNodeToAnalyse] = useState(focusedNode || null);
  const [seuToAnalyse, setSeuToAnalyse] = useState(focusedSeu || null);
  const [analysisMode, setAnalysisMode] = useState(initialMode || 'node'); // or 'seu'
  const [viewMode, setViewMode] = useState(null);
  const [aggregate, setAggregate] = useState('hour');
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [seriesColors, setSeriesColors] = useState([]);
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'activeHoursFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_hours');
          case false:
            return t('filter_labels.inactive_hours');
          default:
            return t('filter_labels.all_hours');
        }
      case 'activeDaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_days');
          case false:
            return t('filter_labels.inactive_days');
          default:
            return t('filter_labels.all_days');
        }
      case 'activeHolidaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.holidays_included');
          case false:
            return t('filter_labels.holidays_excluded');
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const [energyConsumptionData, setEnergyConsumptionData] = useState(null);
  const [seuEnergyConsumptionData, setSeuEnergyConsumptionData] = useState([]);
  const [
    baselineSeuEnergyConsumptionData,
    setBaselineSeuEnergyConsumptionData,
  ] = useState(null);
  const [lazyLoadedEnergyConsumptionData, setLazyLoadedEnergyConsumptionData] =
    useState([]);

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [
    getEnergyConsumptionData,
    {
      isLoading: isLoadingEnergyConsumptionData,
      isFetching: isFetchingEnergyConsumptionData,
    },
  ] = useLazyGetEnergyConsumptionQuery();
  const [
    getSeuEnergyConsumptionData,
    {
      isLoading: isLoadingSeuEnergyConsumptionData,
      isFetching: isFetchingSeuEnergyConsumptionData,
    },
  ] = useLazyGetSeuEnergyConsumptionQuery();

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };
  const handleCloseFilterDialog = () => setOpenFilterDialog(false);

  const [filtersOpen, setFiltersOpen] = useState(true);

  const formatDate = (date) => {
    const day = date.setLocale('da').toFormat('d.');
    const month = date.setLocale('da').toFormat('MMMM');
    return `${day} ${month}`;
  };

  const config = useMemo(() => {
    if (typeof duration !== 'number') return null;
    return getDurationConfig(duration) || null; // Ensure it never returns undefined
  }, [duration]);

  useEffect(() => {
    if (!config) return;

    // Determine and set valid initial values
    setViewMode((prev) => {
      if (!prev || !config.chartOptions.includes(prev)) {
        return config.defaultChart;
      }
      return prev;
    });

    setAggregate((prev) => {
      const validAggregates =
        config?.xAxisOptions?.[viewMode || config.defaultChart] || [];
      if (!prev || !validAggregates.includes(prev)) {
        return config.defaultXAxis?.[viewMode || config.defaultChart] || 'hour';
      }
      return prev;
    });
  }, [config, viewMode]);

  const fetchEnergyConsumption = async ({
    nodeId,
    start,
    end,
    filters,
    aggregate,
  }) => {
    try {
      const response = await getEnergyConsumptionData({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId,
        start,
        end,
        aggregate,
        ...filters,
      }).unwrap();

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (error) {
      console.error('Failed to fetch energy data', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i indhentingen af forbrugsdata',
          severity: 'error',
        }),
      );
      return null;
    }
  };

  const handleApplyFilter = async (filters) => {
    const result = await fetchEnergyConsumption({
      nodeId: nodeToAnalyse?.energyTreeNodeId,
      start: timeRange.start,
      end: timeRange.end,
      filters,
      aggregate,
    });

    if (result && result !== null) {
      setLazyLoadedEnergyConsumptionData((prev) =>
        [...prev, result].filter(Boolean),
      );
    } else {
      setLazyLoadedEnergyConsumptionData([]);
    }

    const response = await fetchSeuEnergyConsumption({
      seu: seuToAnalyse,
      start: timeRange?.start,
      end: timeRange?.end,
      filters,
      aggregate,
    });
    setSeuEnergyConsumptionData((prev) => [...prev, response]);
  };

  // const handleApplySeuFilter = async (filters) => {
  //   const response = await fetchSeuEnergyConsumption({
  //     seu: seuToAnalyse,
  //     start: timeRange?.start,
  //     end: timeRange?.end,
  //     filters,
  //     aggregate
  //   });
  //   setSeuEnergyConsumptionData((prev) => [...prev, response]);
  // }

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const fetchData = async () => {
      try {
        const filters = {
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        };

        const result = await getEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId: nodeToAnalyse?.energyTreeNodeId,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          ...filters,
        }).unwrap();

        const resultWithMetadata = result
          ? {
              ...result,
              metadata: {
                ...filters,
                id: Date.now(),
              },
            }
          : null;

        setTimestampSummary(result?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Forbrugeranalysen');
        setLazyLoadedEnergyConsumptionData(
          resultWithMetadata ? [resultWithMetadata] : [],
        );
        // setLazyLoadedEnergyConsumptionData((prev) => [...prev, resultWithMetadata].filter(Boolean));
      } catch (error) {
        dispatch(
          showSnackbar({
            message: 'Der opstod en fejl i hentningen af forbrugsdata',
            severity: 'error',
          }),
        );
        setLazyLoadedEnergyConsumptionData([]);
        setTimestampSummary(null);
        setTimestampSummarySetBy(null);
      }
    };

    fetchData();
  }, [selectedAccount, selectedEnergyGroup, nodeToAnalyse]);

  const refetchLazyLoadedData = async ({ aggregate, start, end }) => {
    const updatedData = await Promise.all(
      lazyLoadedEnergyConsumptionData.map(async (entry) => {
        const { metadata } = entry;
        const filters = {
          activeDaysFilter: metadata.activeDaysFilter,
          activeHoursFilter: metadata.activeHoursFilter,
          activeHolidaysFilter: metadata.activeHolidaysFilter,
        };

        const result = await fetchEnergyConsumption({
          nodeId: nodeToAnalyse?.energyTreeNodeId, // ✅ use series-specific node
          start,
          end,
          filters,
          aggregate,
        });

        return result
          ? {
              ...result,
              metadata: {
                ...metadata, // keep ID and filters
              },
            }
          : entry; // fallback to original if fetch fails
      }),
    );

    setLazyLoadedEnergyConsumptionData(updatedData.filter(Boolean));
  };

  const fetchSeuEnergyConsumption = async ({
    seu,
    start,
    end,
    filters,
    aggregate,
  }) => {
    try {
      const response = await getSeuEnergyConsumptionData({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        seu,
        start,
        end,
        aggregate,
        ...filters,
      }).unwrap();

      if (response?.EnergySummary?.TimestampsSummary) {
        setTimestampSummary(response?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Forbrugeranalysen');
      } else {
        setTimestampSummary(null);
        setTimestampSummarySetBy(null);
      }

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (error) {
      console.error('Failed to fetch energy data', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i indhentingen af SEU data',
          severity: 'error',
        }),
      );
      return null;
    }
  };

  const refetchData = async ({ aggregate, start, end }) => {
    const updatedData = await Promise.all(
      seuEnergyConsumptionData.map(async (entry) => {
        const { metadata } = entry;
        const filters = {
          activeDaysFilter: metadata.activeDaysFilter,
          activeHoursFilter: metadata.activeHoursFilter,
          activeHolidaysFilter: metadata.activeHolidaysFilter,
        };

        const result = await fetchSeuEnergyConsumption({
          seu: seuToAnalyse,
          start,
          end,
          filters,
          aggregate,
        });

        setTimestampSummary(result?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Forbrugeranalysen');

        return result; // fallback to old entry if request fails
      }),
    );

    if (updatedData) {
      setSeuEnergyConsumptionData(updatedData);
    }
  };

  useEffect(() => {
    if (!selectedEnergyGroup || !nodeToAnalyse) return;

    refetchLazyLoadedData({
      aggregate,
      start: timeRange.start,
      end: timeRange.end,
    });
  }, [timeRange, aggregate, viewMode, nodeToAnalyse]);

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const fetchBaselineEnergyData = async () => {
      try {
        const result = await getEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId: nodeToAnalyse?.energyTreeNodeId,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        }).unwrap();

        const baselineWithMeta = {
          ...result,
          metadata: {
            id: 'BASELINE', // or keep this empty if not needed
          },
        };

        setEnergyConsumptionData(baselineWithMeta);
        setTimestampSummary(baselineWithMeta?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Forbrugeranalysen');
      } catch (error) {
        console.error('Baseline energy fetch failed', error);
        setEnergyConsumptionData(null);
        setTimestampSummary(null);
        setTimestampSummarySetBy(null);
        dispatch(
          showSnackbar({
            message: 'Fejl i hentning af samlet energiforbrug',
            severity: 'error',
          }),
        );
      }
    };

    fetchBaselineEnergyData();
  }, [selectedAccount, selectedEnergyGroup, nodeToAnalyse, timeRange]);

  useEffect(() => {
    if (!seuToAnalyse) return;

    const fetchBaselineEnergyData = async () => {
      try {
        const result = await getSeuEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          seu: seuToAnalyse,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        }).unwrap();

        setTimestampSummary(result?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Forbrugeranalysen');

        const baselineWithMeta = {
          ...result,
          metadata: {
            id: 'BASELINE', // or keep this empty if not needed
          },
        };

        setBaselineSeuEnergyConsumptionData(baselineWithMeta);
      } catch (error) {
        console.error('Baseline energy fetch failed', error);
        setBaselineSeuEnergyConsumptionData(null);
        setTimestampSummary(null);
        dispatch(
          showSnackbar({
            message: 'Fejl i hentning af samlet energiforbrug',
            severity: 'error',
          }),
        );
      }
    };
    fetchBaselineEnergyData();
  }, [selectedAccount, selectedEnergyGroup, seuToAnalyse, timeRange]);

  useEffect(() => {
    if (!seuToAnalyse) return;

    const fetchData = async () => {
      const response = await fetchSeuEnergyConsumption({
        seu: seuToAnalyse,
        start: timeRange?.start,
        end: timeRange?.end,
        filters: {
          activeDaysFilter: undefined,
          activeHoursFilter: undefined,
          activeHolidaysFilter: undefined,
        },
        aggregate,
      });
      if (response) {
        setSeuEnergyConsumptionData([response]);
        setTimestampSummary(response?.EnergySummary?.TimestampsSummary);
      }
    };

    fetchData();
  }, [
    seuToAnalyse,
    selectedEnergyGroup?.energyGroupId,
    aggregate,
    viewMode,
    timeRange?.start,
    timeRange?.end,
    duration,
  ]);

  useEffect(() => {
    if (seuEnergyConsumptionData && seuEnergyConsumptionData.length > 0) {
      refetchData({
        seu: seuToAnalyse,
        aggregate,
        start: timeRange.start,
        end: timeRange.end,
      });
    }
  }, [timeRange, aggregate, seuToAnalyse]);

  const validChartOptions = config?.chartOptions || []; // Available chart types
  const validXAxisOptions = config?.xAxisOptions?.[viewMode] || []; // Available X-Axis granularity

  useEffect(() => {
    if (!config) return;

    const defaultChart = config.defaultChart;
    const defaultXAxis = config.defaultXAxis?.[defaultChart] || 'hour';

    // Only override if values are not valid
    setViewMode((prev) => {
      if (!prev || !config.chartOptions.includes(prev)) {
        return defaultChart;
      }
      return prev;
    });

    setAggregate((prev) => {
      const currentView = viewMode || defaultChart;
      const validAggregates = config?.xAxisOptions?.[currentView] || [];

      if (!prev || !validAggregates.includes(prev)) {
        return config.defaultXAxis?.[currentView] || 'hour';
      }
      return prev;
    });
  }, [config, viewMode]);

  const getTooltipTitle = (item) => {
    const labelMap = {
      hour: t('common.hour'),
      day: t('common.day'),
      week: t('common.week'),
      month: t('common.month'),
    };

    const isValidXAxis = validXAxisOptions.includes(item);

    // Tooltip logic
    if (item === 'hour' && viewMode !== 'line') {
      return t('consumption_analysis.only_available_for_linear_series');
    }

    if (['day', 'week', 'month'].includes(item) && viewMode === 'line') {
      return t('consumption_analysis.only_available_for_column_series');
    }

    if (!isValidXAxis) {
      return t('consumption_analysis.not_available_for_selected_period');
    }

    return `${t('consumption_analysis.show_per')} ${labelMap[item] || item}`;
  };

  // Ensure `nodeToAnalyse` starts as a valid node (fix for initial load issue)
  useEffect(() => {
    if (
      !nodeToAnalyse ||
      !selectedEnergyGroup.energyTreeNodes.find(
        (node) => node.energyTreeNodeId === nodeToAnalyse.energyTreeNodeId,
      )
    ) {
      setNodeToAnalyse(selectedEnergyGroup.energyTreeNodes[0]); // Set first available node as default
    }
  }, [nodeToAnalyse, selectedEnergyGroup]);

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();

    // First pass: add all nodes
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      map.set(node.energyTreeNodeId, { ...node, children: [] });
    });

    // Second pass: link children to parents
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [],
    [nodeToAnalyse, nodeLookup],
  );
  const parent = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.parentId) || null,
    [nodeToAnalyse, nodeLookup],
  );

  const seuInputRef = useRef(null);

  const anchorRef = useRef(null);
  const [openAggregationMenu, setOpenAggregationMenu] = useState(false);
  const [dataSeriesAggregate, setDataSeriesAggregate] = useState({
    value: 'mean',
    label: t('common.mean'),
  });

  const handleToggleAggregationMenu = () => {
    setOpenAggregationMenu((prev) => !prev);
  };

  const handleCloseAggregationMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenAggregationMenu(false);
  };
  const handleSelectAggregation = (option) => {
    setOpenAggregationMenu(false);
    setDataSeriesAggregate(option);
  };

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([type, nodes]) =>
        nodes.some((node) => node?.dataStreams?.length > 0),
      )
      .map(([type]) => type)
      .filter(
        (type) =>
          type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
      );
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse);
    }
  }, [analysisMode]);

  const options = [
    { value: 'sum', label: t('common.sum') },
    { value: 'mean', label: t('common.mean') },
    { value: 'min', label: t('common.min') },
    { value: 'max', label: t('common.max') },
  ];

  // const timestampsSummary = useMemo(() => {
  //   const data =
  //     analysisMode === 'node'
  //       ? lazyLoadedEnergyConsumptionData
  //       : seuEnergyConsumptionData;

  //   if (!data || data.length === 0) return null;
  //   let result = combineTimestampsSummaryList(
  //     data.map((data) =>
  //       convertTimestampsSummaryToDateTimes(
  //         data?.EnergySummary?.TimestampsSummary,
  //       ),
  //     ),
  //   );
  //   return result;
  // }, [analysisMode, lazyLoadedEnergyConsumptionData, seuEnergyConsumptionData]);

  if (!viewMode || !aggregate) return null; // or <LoadingSkeleton />
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          item
          md={12}
          lg={filtersOpen ? 9 : 12}
          sx={{
            transition: 'flex-basis 0.3s ease-in-out', // smooth resize
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ListItemText
                        primary={analysisMode === 'node' ? `Node` : `SEU`}
                        primaryTypographyProps={{
                          color: 'text.secondary',
                          variant: 'body2',
                        }}
                        secondary={
                          analysisMode === 'node'
                            ? `${nodeToAnalyse?.name}`
                            : `${seuToAnalyse}`
                        }
                        secondaryTypographyProps={{
                          color: 'text.primary',
                          variant: 'h6',
                          fontWeight: 'bold',
                        }}
                      />
                      {analysisMode === 'node' ? (
                        <TimestampSummaryWarningIcon node={nodeToAnalyse} />
                      ) : (
                        <TimestampSummarySeuWarningIcon
                          seu={seuToAnalyse}
                          energyGroupId={selectedEnergyGroup.energyGroupId}
                        />
                      )}
                      <Tooltip
                        title={`${t('consumption_analysis.sum_consumption_for')} ${nodeToAnalyse?.name}`}
                        placement="right"
                      >
                        <InfoRoundedIcon
                          fontSize="small"
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                        />
                      </Tooltip>
                    </Box>

                    {analysisMode === 'node' && (
                      <Box
                        flexDirection={'row'}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          fontWeight="bold"
                        >
                          {energyConsumptionData
                            ? formatNumber(
                                energyConsumptionData.EnergySummary?.TotalEnergyUsage.toFixed(
                                  0,
                                ),
                              )
                            : '-'}{' '}
                          kWh
                        </Typography>
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          fontWeight="bold"
                        ></Typography>
                      </Box>
                    )}

                    {analysisMode === 'seu' && (
                      <Box
                        flexDirection={'row'}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          fontWeight="bold"
                        >
                          {baselineSeuEnergyConsumptionData
                            ? formatNumber(
                                baselineSeuEnergyConsumptionData.EnergySummary.TotalEnergyUsage.toFixed(
                                  0,
                                ),
                              )
                            : '-'}{' '}
                          kWh
                        </Typography>
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          fontWeight="bold"
                        ></Typography>
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('consumption_analysis.in_period')}{' '}
                      {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : t('consumption_analysis.select_period')}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Tooltip
                        title={
                          aggregate === 'hour'
                            ? t(
                                'consumption_analysis.aggregate_change_unavailable',
                              )
                            : t('consumption_analysis.change_aggregate')
                        }
                        placement="top"
                      >
                        <div>
                          <Button
                            ref={anchorRef}
                            onClick={handleToggleAggregationMenu}
                            startIcon={<FunctionsRoundedIcon />}
                            disabled={aggregate === 'hour'}
                            sx={{
                              p: 1,
                              px: 2,
                              borderRadius: 3,
                              color: 'text.primary',
                              width: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              mr: 1,
                              textTransform: 'none', // optional: to keep label casing
                            }}
                          >
                            {dataSeriesAggregate?.label ?? t('common.select')}
                          </Button>
                        </div>
                      </Tooltip>

                      <ToggleButtonGroup
                        className="non-printable"
                        value={viewMode}
                        exclusive
                        onChange={(event, newView) => {
                          if (
                            newView !== null &&
                            validChartOptions.includes(newView)
                          ) {
                            setViewMode(newView);
                          }
                        }}
                      >
                        <Tooltip
                          title={
                            !validChartOptions.includes('line')
                              ? t(
                                  'consumption_analysis.not_available_for_selected_period',
                                )
                              : t(
                                  'consumption_analysis.display_sum_of_consumption_over_time',
                                )
                          }
                          placement="top"
                        >
                          <span>
                            <ToggleButton
                              value="line"
                              size="small"
                              disabled={!validChartOptions.includes('line')}
                              sx={{ borderRadius: 3, p: 1.1 }}
                            >
                              <ShowChartRoundedIcon />
                            </ToggleButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={t(
                            'consumption_analysis.display_sum_of_consumption_over_time',
                          )}
                          placement="top"
                        >
                          <span>
                            <ToggleButton
                              value="column"
                              size="small"
                              disabled={!validChartOptions.includes('column')}
                              sx={{ borderRadius: 3, p: 1.1 }}
                            >
                              <BarChartRoundedIcon />
                            </ToggleButton>
                          </span>
                        </Tooltip>
                      </ToggleButtonGroup>

                      <Popper
                        open={openAggregationMenu}
                        anchorEl={anchorRef.current}
                        placement="bottom-end"
                        transition
                        disablePortal
                        sx={{ zIndex: 1301 }}
                      >
                        {({ TransitionProps }) => (
                          <Grow
                            {...TransitionProps}
                            style={{ transformOrigin: 'left top' }}
                          >
                            <Paper
                              elevation={0}
                              variant="outlined"
                              sx={{ mt: 0.5, borderRadius: 2 }}
                            >
                              <ClickAwayListener
                                onClickAway={handleCloseAggregationMenu}
                              >
                                <MenuList
                                  autoFocusItem={open}
                                  dense
                                  sx={{ minWidth: 150, p: 1 }}
                                >
                                  {options.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      onClick={() =>
                                        handleSelectAggregation(option)
                                      }
                                      sx={{
                                        py: '6px',
                                        px: '12px',
                                        borderRadius: '5px',
                                        '&:hover': {
                                          bgcolor: 'action.hover',
                                          transition: 'all 0.3s ease',
                                        },
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {option.label}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>

                      <ToggleButtonGroup
                        value={aggregate}
                        exclusive
                        onChange={(event, newValue) => {
                          if (newValue !== null) setAggregate(newValue);
                        }}
                        fullWidth
                        sx={{ ml: 1 }}
                      >
                        {['hour', 'day', 'week', 'month', 'year'].map(
                          (item) => {
                            const isDisabled =
                              !validXAxisOptions.includes(item);
                            return (
                              <Tooltip
                                key={item}
                                title={getTooltipTitle(item)}
                                placement="top"
                              >
                                {/* Wrap in span to make Tooltip work on disabled buttons */}
                                <span>
                                  <ToggleButton
                                    value={item}
                                    size="small"
                                    disabled={isDisabled}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: 3,
                                      p: 1.2,
                                      pointerEvents: isDisabled
                                        ? 'none'
                                        : 'auto', // important for Tooltip to show
                                    }}
                                  >
                                    {item === 'hour' && t('common.hour')}
                                    {item === 'day' && t('common.day')}
                                    {item === 'week' && t('common.week')}
                                    {item === 'month' && t('common.month')}
                                    {item === 'year' && t('common.year')}
                                  </ToggleButton>
                                </span>
                              </Tooltip>
                            );
                          },
                        )}
                      </ToggleButtonGroup>

                      <Tooltip
                        className="non-printable"
                        title={`${filtersOpen ? t('consumption_analysis.tooltips.hide') : t('consumption_analysis.tooltips.show')}`}
                        placement="top"
                      >
                        <IconButton
                          onClick={() => setFiltersOpen(!filtersOpen)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                            ml: 4,
                          }}
                        >
                          {filtersOpen ? (
                            <KeyboardArrowRightRoundedIcon />
                          ) : (
                            <KeyboardArrowLeftRoundedIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Stack>
              {analysisMode === 'node' && (
                <Suspense fallback={<div>Loading...</div>}>
                  <EnergyAnalysisSingleSeriesChart // Single Series Chart to slice data
                    // nodeData={energyConsumptionData}
                    lazyLoadedEnergyConsumptionData={
                      lazyLoadedEnergyConsumptionData
                    }
                    isLoading={
                      isLoadingEnergyConsumptionData ||
                      isFetchingEnergyConsumptionData
                    }
                    viewMode={viewMode}
                    aggregate={aggregate}
                    dataSeriesAggregate={dataSeriesAggregate}
                    selectedEnergyGroup={selectedEnergyGroup}
                    setSeriesColors={setSeriesColors}
                  />
                </Suspense>
              )}

              {analysisMode === 'seu' && (
                <Suspense fallback={<div>Loading...</div>}>
                  <EnergyAnalysisSeuSingleSeriesChart
                    selectedEnergyGroup={selectedEnergyGroup}
                    aggregate={aggregate}
                    timeRange={timeRange}
                    data={seuEnergyConsumptionData}
                    dataSeriesAggregate={dataSeriesAggregate}
                    isLoading={
                      isLoadingSeuEnergyConsumptionData ||
                      isFetchingSeuEnergyConsumptionData
                    }
                    viewMode={viewMode}
                    setSeriesColors={setSeriesColors}
                  />
                </Suspense>
              )}
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid
            item
            className="non-printable"
            md={12}
            lg={3}
            sx={{
              transition: 'flex-basis 0.3s ease', // smooth collapse/expand
            }}
          >
            <Box
              sx={{
                maxHeight: filtersOpen ? 1000 : 0, // set a maxHeight large enough to fit content
                overflow: 'hidden',
                transition: 'max-height 0.3s ease',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      {analysisMode === 'seu' && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                fontWeight="bold"
                              >
                                {t('common.series')}
                              </Typography>
                              <Tooltip
                                title={t(
                                  'consumption_analysis.series_description',
                                )}
                                placement="left"
                              >
                                <InfoRoundedIcon
                                  fontSize="small"
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.mode === 'light'
                                        ? 'lightgray'
                                        : 'gray',
                                    ml: 1,
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            <Button
                              onClick={handleOpenFilterDialog}
                              variant="contained"
                              endIcon={<AddRoundedIcon />}
                            >
                              {t('consumption_analysis.add_series')}
                            </Button>
                          </Box>

                          <List dense>
                            {seuEnergyConsumptionData?.map((series, index) => {
                              const seriesColor =
                                seriesColors[index]?.color ?? '#ccc';

                              return (
                                <ListItem
                                  key={series?.metadata?.id || index}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    mb: 1,
                                    alignItems: 'center',
                                  }}
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      onClick={() => {
                                        const idToRemove = series?.metadata?.id;
                                        setData((prev) =>
                                          prev.filter(
                                            (s) =>
                                              s.metadata?.id !== idToRemove,
                                          ),
                                        );
                                        setSeriesColors((prev) =>
                                          prev.filter(
                                            (_, i) =>
                                              data[i]?.metadata?.id !==
                                              idToRemove,
                                          ),
                                        );
                                      }}
                                    >
                                      <DeleteRoundedIcon />
                                    </IconButton>
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: seriesColor,
                                      mr: 2,
                                      mt: 0.5,
                                    }}
                                  />
                                  <ListItemText
                                    primary={`${seuToAnalyse} - Serie ${index + 1}`}
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        component="div"
                                      >
                                        <i>
                                          {t(
                                            'consumption_analysis.used_filters',
                                          )}
                                          :{' '}
                                        </i>
                                        {[
                                          getFilterLabel(
                                            'activeDaysFilter',
                                            series?.metadata?.activeDaysFilter,
                                          ),
                                          getFilterLabel(
                                            'activeHoursFilter',
                                            series?.metadata?.activeHoursFilter,
                                          ),
                                          getFilterLabel(
                                            'activeHolidaysFilter',
                                            series?.metadata
                                              ?.activeHolidaysFilter,
                                          ),
                                        ]
                                          .filter(Boolean)
                                          .map((text, i) => (
                                            <span key={i}>
                                              {i > 0 && ', '}
                                              <strong>{text}</strong>
                                            </span>
                                          ))}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      )}

                      {analysisMode === 'node' && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                fontWeight="bold"
                              >
                                {t('common.series')}
                              </Typography>
                              <Tooltip
                                title={t(
                                  'consumption_analysis.series_description',
                                )}
                                placement="right"
                              >
                                <InfoRoundedIcon
                                  fontSize="small"
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.mode === 'light'
                                        ? 'lightgray'
                                        : 'gray',
                                    ml: 1,
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            <Button
                              onClick={handleOpenFilterDialog}
                              variant="text"
                              endIcon={<AddRoundedIcon />}
                            >
                              {t('consumption_analysis.add_series')}
                            </Button>
                          </Box>

                          <List dense>
                            {lazyLoadedEnergyConsumptionData?.map(
                              (series, index) => {
                                const seriesColor =
                                  seriesColors[index]?.color ?? '#ccc';

                                return (
                                  <ListItem
                                    key={series?.metadata?.id || index}
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 2,
                                      mb: 1,
                                      alignItems: 'center',
                                    }}
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        onClick={() => {
                                          const idToRemove =
                                            series?.metadata?.id;
                                          setLazyLoadedEnergyConsumptionData(
                                            (prev) =>
                                              prev.filter(
                                                (s) =>
                                                  s.metadata?.id !== idToRemove,
                                              ),
                                          );
                                          setSeriesColors((prev) =>
                                            prev.filter(
                                              (_, i) =>
                                                lazyLoadedEnergyConsumptionData[
                                                  i
                                                ]?.metadata?.id !== idToRemove,
                                            ),
                                          );
                                        }}
                                      >
                                        <DeleteRoundedIcon />
                                      </IconButton>
                                    }
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: seriesColor,
                                        mr: 2,
                                        mt: 0.5,
                                      }}
                                    />
                                    <ListItemText
                                      primary={`${nodeToAnalyse?.name} - Serie ${index + 1}`}
                                      secondary={
                                        <Typography
                                          variant="caption"
                                          component="div"
                                        >
                                          <i>
                                            {t(
                                              'consumption_analysis.used_filters',
                                            )}
                                            :{' '}
                                          </i>
                                          {[
                                            getFilterLabel(
                                              'activeDaysFilter',
                                              series?.metadata
                                                ?.activeDaysFilter,
                                            ),
                                            getFilterLabel(
                                              'activeHoursFilter',
                                              series?.metadata
                                                ?.activeHoursFilter,
                                            ),
                                            getFilterLabel(
                                              'activeHolidaysFilter',
                                              series?.metadata
                                                ?.activeHolidaysFilter,
                                            ),
                                          ]
                                            .filter(Boolean)
                                            .map((text, i) => (
                                              <span key={i}>
                                                {i > 0 && ', '}
                                                <strong>{text}</strong>
                                              </span>
                                            ))}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                );
                              },
                            )}
                          </List>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs sx={{ mt: 2 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs>
                            {analysisMode === 'node' ? (
                              <>
                                <EnergyTreeNodeAutocomplete
                                  nodeOptions={
                                    selectedEnergyGroup?.energyTreeNodes
                                  }
                                  selectedNode={nodeToAnalyse}
                                  onChange={(newValue) => {
                                    if (newValue) setNodeToAnalyse(newValue);
                                  }}
                                />
                                <Box
                                  mt={1}
                                  display="flex"
                                  justifyContent="flex-start"
                                >
                                  <Button
                                    variant="text"
                                    onClick={() => {
                                      setAnalysisMode('seu');
                                    }}
                                  >
                                    {t('consumption_analysis.select_seu')}
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Autocomplete
                                  options={validSeuOptions}
                                  getOptionLabel={(option) => option}
                                  value={seuToAnalyse}
                                  onChange={(e, newValue) => {
                                    setSeuToAnalyse(newValue);
                                  }}
                                  disableClearable
                                  renderOption={(props, option) => {
                                    const isDisabled =
                                      !validSeuOptions.includes(option);
                                    return (
                                      <li
                                        {...props}
                                        key={option}
                                        style={{
                                          ...props.style,
                                          opacity: isDisabled ? 0.4 : 1,
                                          pointerEvents: isDisabled
                                            ? 'none'
                                            : 'auto',
                                          cursor: isDisabled
                                            ? 'not-allowed'
                                            : 'pointer',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            backgroundColor:
                                              getTypeColor(option),
                                            position: 'relative', // Changed from 'absolute' to maintain layout
                                            borderRadius: '25px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            border: `1px solid divider`,
                                            marginRight: 1,
                                          }}
                                        >
                                          {' '}
                                          <i
                                            style={{
                                              color: 'white',
                                            }}
                                            className={`fa-solid ${getTypeIcon(option)}`}
                                          ></i>
                                        </Box>
                                        {option}
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t(
                                        'consumption_analysis.select_seu',
                                      )}
                                      inputRef={seuInputRef}
                                      autoFocus={false}
                                      variant="standard"
                                      // sx={{
                                      //   '& .MuiInputBase-root': {
                                      //     borderRadius: '10px',
                                      //   },
                                      // }}
                                      InputProps={{
                                        ...params.InputProps,
                                        disableUnderline: true,
                                        startAdornment: seuToAnalyse ? (
                                          <InputAdornment position="start">
                                            <Avatar
                                              variant="round"
                                              size="small"
                                              sx={{
                                                backgroundColor:
                                                  getTypeColor(seuToAnalyse) ||
                                                  '#555',
                                                color: '#fff',
                                                height: 32,
                                                width: 32,
                                                fontSize: 16,
                                              }}
                                            >
                                              <i
                                                className={`fas ${getTypeIcon(seuToAnalyse)}`}
                                              ></i>
                                            </Avatar>
                                          </InputAdornment>
                                        ) : null,
                                      }}
                                    />
                                  )}
                                />

                                <Box
                                  mt={1}
                                  display="flex"
                                  justifyContent="flex-start"
                                >
                                  <Button
                                    variant="text"
                                    onClick={() => {
                                      setAnalysisMode('node');
                                      // setSeuToAnalyse(null);
                                    }}
                                  >
                                    {t(
                                      'consumption_analysis.select_measurement_point',
                                    )}
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Grid>

                          <Grid item>
                            <IconButton
                              onClick={() => parent && setNodeToAnalyse(parent)}
                              disabled={!parent}
                              sx={{
                                p: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                              }}
                            >
                              <ArrowUpwardRoundedIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                        {analysisMode === 'node' && (
                          <List
                            dense
                            sx={{
                              maxHeight: 425,
                              overflowY: 'auto',
                            }}
                            subheader={
                              <ListSubheader
                                component="div"
                                id="nested-list-subheader"
                              >
                                {t('consumption_analysis.breakdown_for')}{' '}
                                {nodeToAnalyse?.name}:
                              </ListSubheader>
                            }
                          >
                            {children.length > 0 ? (
                              children.map((child) => (
                                <ListItemButton
                                  key={child.energyTreeNodeId}
                                  onClick={() => setNodeToAnalyse(child)}
                                  sx={{ borderRadius: 2 }}
                                >
                                  <ListItemIcon>
                                    <Avatar
                                      variant="round"
                                      size="small"
                                      sx={{
                                        backgroundColor:
                                          getTypeColor(child.type) || '#555',
                                        color: '#fff',
                                        height: 32,
                                        width: 32,
                                        fontSize: 16,
                                      }}
                                    >
                                      <i
                                        className={`fas ${getTypeIcon(child.type)}`}
                                      ></i>
                                    </Avatar>
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={child.name}
                                    secondary={child.type}
                                  />
                                </ListItemButton>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  textAlign: 'center',
                                  color: 'gray',
                                }}
                              >
                                {t('consumption_analysis.no_further_breakdown')}
                              </Typography>
                            )}
                          </List>
                        )}
                      </>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>

      <Suspense fallback={<div>Loading...</div>}>
        <EnergyAnalyzerFilterDialog
          open={openFilterDialog}
          handleClose={handleCloseFilterDialog}
          handleApply={handleApplyFilter}
          selectedEnergyGroup={selectedEnergyGroup}
          startHour={selectedEnergyGroup?.options?.workingDayStart}
          endHour={selectedEnergyGroup?.options?.workingDayEnd}
        />
      </Suspense>
    </Box>
  );
};

export default EnergyAnalysisTab;
